import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { togglePaymentPlanFrequency } from '../../../actions';
import { currencyFormat } from '../../../util';
import { BILLING_CYCLE_TYPE } from '../../../constants';
import './PaymentSummary.css';
import { RadioButton } from '../../common/RadioButton';

const PaymentSummary = () => {
  const { t } = useTranslation('enrollment');
  const store = useSelector(({ quoting }) => quoting, shallowEqual);
  const dispatch = useDispatch();
  const {
    isMonthlyPlan,
    quoteSelectedValues,
    data,
    paymentMethod,
  } = store;
  const { InsuranceProductFeeEB } = data.insuranceProductEB;
  const paymentType = paymentMethod.paymentMethodType.value;

  function getAnnualyFee() {
    const { annualy } = BILLING_CYCLE_TYPE;
    const fee = InsuranceProductFeeEB
      .find((feeEB) => feeEB.InsuranceFeeEB.BillingCycleType === annualy
        && feeEB.InsuranceFeeEB.PaymentType === paymentType);

    return fee;
  }

  function getMonthlyFee() {
    const { monthly } = BILLING_CYCLE_TYPE;
    const fee = InsuranceProductFeeEB
      .find((feeEB) => feeEB.InsuranceFeeEB.BillingCycleType === monthly
        && feeEB.InsuranceFeeEB.PaymentType === paymentType);

    return fee;
  }

  useEffect(() => {
    dispatch(togglePaymentPlanFrequency({ isMonthly: true }));
  }, [dispatch]);

  function getTotalMonthlyAmount() {
    const totalMonthlyAmount = quoteSelectedValues
      .reduce((prev, next) => prev + next.totalMontly, 0);
    const [dollar, cent] = currencyFormat({ value: totalMonthlyAmount })
      .split('.');

    return { cent: `.${cent}`, dollar };
  }

  function getTotalAnnualAmount() {
    const totalAnnualAmount = quoteSelectedValues
      .reduce((prev, next) => prev + next.totalAnnual, 0);
    const [dollar, cent] = currencyFormat({ value: totalAnnualAmount })
      .split('.');

    return { cent: `.${cent}`, dollar };
  }

  function getMonthlyFeeLegend() {
    const fee = getMonthlyFee();

    if (!fee) {
      return '';
    }

    const { InsuranceFeeEB } = fee;

    return InsuranceFeeEB.DisplayText.replace('{0}', InsuranceFeeEB.Amount);
  }

  function getAnnualyFeeLegend() {
    const fee = getAnnualyFee();

    if (!fee) {
      return 'Save on any monthly processing fees.';
    }

    const { InsuranceFeeEB } = fee;

    return InsuranceFeeEB.DisplayText.replace('{0}', InsuranceFeeEB.Amount);
  }

  function renderMonthlyFee() {
    const { cent, dollar } = getTotalMonthlyAmount();

    return (
      <div className="Billing-cycle-row Billing-cycle-price">
        <div className="Billing-cycle-price-container">
          <span className="Billing-cycle-price-currency-cent">$</span>

          <span className="Billing-cycle-price-dollar">
            {dollar}
          </span>

          <span className="Billing-cycle-price-currency-cent">
            {cent}
          </span>
        </div>

        <span className="Billing-cycle-price-tag">
          &frasl;
          {' '}
          {t('billingCycle.month')}
        </span>
      </div>
    );
  }

  function renderAnnualFee() {
    const { cent, dollar } = getTotalAnnualAmount();
    return (
      <div className="Billing-cycle-row Billing-cycle-price">
        <span className="Billing-cycle-price-currency-cent">$</span>

        <span className="Billing-cycle-price-dollar">
          {dollar}
        </span>

        <span className="Billing-cycle-price-currency-cent">
          {cent}
        </span>

        <span className="Billing-cycle-price-tag">
          &frasl;
          {' '}
          {t('billingCycle.year')}
        </span>
      </div>
    );
  }

  const onBillingCycleChanged = (event) => {
    const isMonthly = !!Number(event.target.value);
    dispatch(togglePaymentPlanFrequency({ isMonthly }));
  };

  const getBillingCycleAriaLabel = (isMonthlyCard = false) => {
    let label = 'Billing cycle annual';
    let quantityAmount = getTotalAnnualAmount;
    let feeLabelLegend = getAnnualyFeeLegend;
    let currentLabelSelected = '';
    let bilingPaymentCycle = 'per year.';

    const monthlyLabelSelected =
      isMonthlyPlan && isMonthlyCard ? ' selected.' : ' not selected.';
    const annualLabelSelected =
      !isMonthlyPlan && !isMonthlyCard ? ' selected.' : 'not selected.';

    if (isMonthlyCard) {
      label = 'Billing cycle monthly';
      quantityAmount = getTotalMonthlyAmount;
      feeLabelLegend = getMonthlyFeeLegend;

      currentLabelSelected = monthlyLabelSelected;
      bilingPaymentCycle = 'per month.';
    } else {
      currentLabelSelected = annualLabelSelected;
    }

    const { cent, dollar } = quantityAmount();
    const feeLabel = feeLabelLegend();
    return `${label} ${dollar}${cent} ${bilingPaymentCycle} `
      + `${feeLabel} ${currentLabelSelected}`;
  };

  return (
    <>
      <h1
        className="Billing-cycle-title"
        id="billing-cycle-title"
        tabIndex={-1}
      >
        {t('billingCycle.title')}
      </h1>

      <div className="Billing-cycle-section Billing-cycle-row">
        <div
          aria-label={getBillingCycleAriaLabel(true)}
          aria-live={isMonthlyPlan ? 'polite' : 'off'}
          className="Billing-cycle-column Billing-cycle-div"
          data-checked={isMonthlyPlan}
          id="plan-sumary-monthly"
        >
          <RadioButton
            aria-label="plan-sumary-monthly"
            checked={isMonthlyPlan}
            className="Billing-cycle-radio-pos"
            containerClassName="Billing-cycle-radio-padding"
            id="monthly"
            name="billing-cycle"
            onChange={onBillingCycleChanged}
            value={1}
          >
            <div className="Billing-cycle-colmn Billing-cycle-plan-container">
              <div className="Billing-cycle-row Billing-cycle-plan-title">
                <span>{t('billingCycle.monthly')}</span>

                {renderMonthlyFee()}
              </div>

              <div className="Billing-cycle-row Billing-cycle-additional-fee">
                <span>
                  {getMonthlyFeeLegend()}
                </span>
              </div>
            </div>
          </RadioButton>
        </div>

        <div
          aria-label={getBillingCycleAriaLabel(false)}
          aria-live={!isMonthlyPlan ? 'polite' : 'off'}
          className="Billing-cycle-column Billing-cycle-div"
          data-checked={!isMonthlyPlan}
          id="plan-sumary-annual"
        >
          <RadioButton
            aria-label="plan-sumary-annual"
            checked={!isMonthlyPlan}
            className="Billing-cycle-radio-pos"
            containerClassName="Billing-cycle-radio-padding"
            id="annual"
            name="billing-cycle"
            onChange={onBillingCycleChanged}
            value={0}
          >
            <div className="Billing-cycle-colmn Billing-cycle-plan-container">
              <div className="Billing-cycle-row Billing-cycle-plan-title">
                <span>{t('billingCycle.annually')}</span>

                {renderAnnualFee()}
              </div>

              <div className="Billing-cycle-row Billing-cycle-additional-fee">
                <span>
                  {getAnnualyFeeLegend()}
                </span>
              </div>
            </div>
          </RadioButton>
        </div>
      </div>
    </>
  );
};

export default PaymentSummary;
